type LogLevel = "debug" | "info" | "warn" | "error";

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// The browser has no `process`: the field app's upload queue logs from there.
const minLevel: LogLevel =
  typeof process === "undefined"
    ? "info"
    : (process.env.LOG_LEVEL as LogLevel) ||
      (process.env.NODE_ENV === "production" ? "info" : "debug");

function emit(level: LogLevel, message: string, data?: Record<string, unknown>) {
  if (LOG_LEVELS[level] < LOG_LEVELS[minLevel]) return;

  const entry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    ...data,
  };

  const line = JSON.stringify(entry);

  if (level === "error") {
    console.error(line);
  } else if (level === "warn") {
    console.warn(line);
  } else {
    console.log(line);
  }
}

export const logger = {
  debug: (msg: string, data?: Record<string, unknown>) => emit("debug", msg, data),
  info: (msg: string, data?: Record<string, unknown>) => emit("info", msg, data),
  warn: (msg: string, data?: Record<string, unknown>) => emit("warn", msg, data),
  error: (msg: string, data?: Record<string, unknown>) => emit("error", msg, data),

  /**
   * Log an HTTP request with duration.
   *
   * `ip` is a required parameter rather than something this function reads off
   * `X-Forwarded-For` itself: that header is caller-supplied, so behind a proxy
   * its leftmost entry is spoofable and the whole chain is attacker-influenced.
   * Resolving it is `getClientIp`'s job (trusted-proxy-aware via
   * `TRUSTED_PROXY_HOPS`) — the same resolver `recordAudit` and the auth rate
   * limiter use, so all three agree on who made the request. Passing it in also
   * keeps this module dependency-free.
   */
  request(
    req: Request,
    ip: string,
    status: number,
    durationMs: number,
    extra?: Record<string, unknown>,
  ) {
    const url = new URL(req.url);
    emit(status >= 500 ? "error" : status >= 400 ? "warn" : "info", "http_request", {
      method: req.method,
      path: url.pathname,
      status,
      durationMs: Math.round(durationMs),
      userAgent: req.headers.get("user-agent") ?? undefined,
      ip,
      ...extra,
    });
  },
};
